import PropTypes from 'prop-types'

import { ActionMenu } from '../../../../components'
import usePlaidRelink from './usePlaidRelink'

const BUSY_LABEL = {
  refreshing: 'Refreshing linked account',
  unlinking: 'Unlinking account',
}

const Menu = ({ relinkItem, onRefresh, onUnlink, pendingAction }) => (
  <ActionMenu
    items={[
      { label: 'Refresh', onClick: onRefresh },
      ...(relinkItem ? [relinkItem] : []),
      { label: 'Unlink', onClick: onUnlink },
    ]}
    ariaLabel='Linked account actions'
    busy={Boolean(pendingAction)}
    busyLabel={BUSY_LABEL[pendingAction]}
  />
)

// Keep the Plaid hook out of the default menu path so Plaid Link is only initialized when needed.
const RelinkableMenu = ({
  accessId,
  getUpdateLinkToken,
  onRelinkSuccess,
  onRelinkError,
  ...menu
}) => {
  const { openRelink } = usePlaidRelink({
    accessId,
    getUpdateLinkToken,
    onSuccess: onRelinkSuccess,
    onError: onRelinkError,
  })

  return <Menu {...menu} relinkItem={{ label: 'Relink', onClick: openRelink }} />
}

/**
 * Overflow menu for a Plaid-linked account: Refresh, Relink where the login has expired,
 * and Unlink.
 *
 * @param {object} props
 * @param {boolean} props.requiresRelink Whether Plaid requires the user to reauthorize the connection.
 * @param {number|null} props.accessId Identifier used to request a relink token.
 * @param {function} props.getUpdateLinkToken Requests an update-mode link token from the store.
 * @param {function} props.onRefresh Re-pulls account data from the institution.
 * @param {function} props.onUnlink Removes the connection to the institution.
 * @param {function} props.onRelinkSuccess Called after the user successfully completes the Plaid flow.
 * @param {function} props.onRelinkError Called when the relink flow cannot be started or completed.
 * @param {'refreshing'|'unlinking'|null} [props.pendingAction] Action in flight; keeps the menu busy.
 */
const LinkedAccountMenu = ({ requiresRelink, ...props }) =>
  requiresRelink ? <RelinkableMenu {...props} /> : <Menu {...props} />

LinkedAccountMenu.propTypes = {
  requiresRelink: PropTypes.bool,
  accessId: PropTypes.number,
  getUpdateLinkToken: PropTypes.func,
  onRefresh: PropTypes.func,
  onUnlink: PropTypes.func,
  onRelinkSuccess: PropTypes.func,
  onRelinkError: PropTypes.func,
  pendingAction: PropTypes.oneOf(['refreshing', 'unlinking']),
}

export default LinkedAccountMenu
